/**
 * Vibecade — public SDK class.
 *
 * Thin composition over submodules in auth.ts, scores.ts, achievements.ts,
 * cloudSave.ts, events.ts. The submodules share a single SdkContext
 * (config, supabase client, logger, player cell) constructed here.
 */
import type {
  VibecadeConfig,
  Player,
  ScoreEntry,
  SubmitScoreOptions,
  SubmitScoreResult,
  LeaderboardOptions,
  AchievementProgress,
  UnlockResult,
  ProgressResult,
  IdentifyOptions,
  EventProperties,
  CloudSave,
} from './types';
import { AuthModule } from './auth';
import { ScoresModule } from './scores';
import { AchievementsModule } from './achievements';
import { CloudSaveModule } from './cloudSave';
import { EventsModule } from './events';
import { createDataClient, createSupabaseClient } from './internal/supabaseClient';
import {
  createLogger,
  resolveConfig,
  type SdkContext,
} from './internal/context';

export class Vibecade {
  /**
   * Cloud save is accessed as a property, not as methods on the class.
   *
   * Typed as the `CloudSave` interface, not as the implementing class: the
   * class's constructor takes the internal SdkContext, and a public field
   * carrying that class published the SDK's entire internal type graph — plus
   * a hard requirement on the peer's types — into `dist/index.d.ts`.
   */
  readonly cloudSave: CloudSave;

  private readonly ctx: SdkContext;
  private readonly auth: AuthModule;
  private readonly scores: ScoresModule;
  private readonly achievements: AchievementsModule;
  private readonly events: EventsModule;

  constructor(config: VibecadeConfig) {
    const resolved = resolveConfig(config);
    const sb = createSupabaseClient(resolved);
    this.ctx = {
      config: resolved,
      sb,
      // Replaced below, once AuthModule exists to answer for the token.
      db: sb,
      accessToken: () => this.auth.requestToken(),
      logger: createLogger(resolved.debug),
      player: { current: null },
    };

    this.auth = new AuthModule(this.ctx);
    this.ctx.db = createDataClient(resolved, this.ctx.accessToken, sb);
    this.scores = new ScoresModule(this.ctx);
    this.achievements = new AchievementsModule(this.ctx);
    this.events = new EventsModule(this.ctx);
    this.cloudSave = new CloudSaveModule(this.ctx);

    this.ctx.logger.debug('Vibecade constructed', {
      gameId: resolved.gameId,
      apiUrl: resolved.apiUrl,
    });
  }

  // ---------- auth ----------

  init(): Promise<void> {
    return this.auth.init();
  }

  isAuthenticated(): boolean {
    return this.auth.isAuthenticated();
  }

  getPlayer(): Player | null {
    return this.auth.getPlayer();
  }

  identify(opts: IdentifyOptions): Promise<void> {
    return this.auth.identify(opts);
  }

  /**
   * Adopt a session a platform endpoint minted for this player, instead of
   * signing in anonymously. For the case where a game runs its own
   * third-party identity flow and the browser has to end up holding the
   * session the platform resolved for it.
   *
   * Two rules decide whether a call is correct. Call it BEFORE `init()`,
   * which signs in anonymously when it finds no session and would leave the
   * game briefly signed in as the wrong person. And it is refused inside an
   * iframe: an embedded game must never hold a renewable credential, because
   * there the launcher is the sole refresh authority.
   *
   * Throws `INVALID_CONFIG` if either token is missing, `AUTH_FAILED` if the
   * session is declined or embedded.
   */
  adoptSession(tokens: { accessToken: string; refreshToken: string }): Promise<Player | null> {
    return this.auth.adoptSession(tokens);
  }

  /**
   * The current session's access token, refreshed if it is close to expiry, or
   * null when there is no session. For calling a PLATFORM endpoint as this
   * player, which is what a game running its own identity flow needs and had
   * no way to do. Never send it to a third-party host: for its lifetime it is
   * this player. Never throws.
   */
  getAccessToken(): Promise<string | null> {
    return this.auth.getAccessToken();
  }

  // ---------- scores ----------

  startPlay(): Promise<{ playToken: string }> {
    return this.scores.startPlay();
  }

  submitScore(opts: SubmitScoreOptions): Promise<SubmitScoreResult> {
    return this.scores.submitScore(opts);
  }

  getLeaderboard(opts?: LeaderboardOptions): Promise<ScoreEntry[]> {
    return this.scores.getLeaderboard(opts);
  }

  // ---------- achievements ----------

  unlockAchievement(achievementId: string): Promise<UnlockResult> {
    return this.achievements.unlock(achievementId);
  }

  setAchievementProgress(achievementId: string, progress: number): Promise<ProgressResult> {
    return this.achievements.setProgress(achievementId, progress);
  }

  getAchievements(): Promise<AchievementProgress[]> {
    return this.achievements.list();
  }

  // ---------- events ----------

  trackEvent(name: string, properties?: EventProperties): void {
    this.events.track(name, properties);
  }

  /**
   * Force an immediate analytics flush. Normally not needed — the SDK
   * flushes every 5s, on 20 buffered events, and on page unload. Useful
   * in tests or before a deliberate reload.
   */
  flushEvents(): Promise<void> {
    return this.events.flush();
  }

  /**
   * Tear down timers + listeners owned by the SDK. Call this if a single
   * page constructs multiple SDK instances and wants deterministic
   * cleanup between them; otherwise the SDK cleans up on page unload.
   */
  dispose(): void {
    this.events.dispose();
    this.auth.dispose();
  }
}
